import { CHAPTERS, WELCOME_END } from "./welcome-scene";

/**
 * The Welcome's player (#1095, #1105, #1141), as pure functions, the prototype's `player()`.
 *
 * The five steps play straight through on one clock, from 0 to `WELCOME_END`: the step on
 * screen is the chapter the clock is in, and crossing into the next never stops, holds or
 * resets the clock (#1141). With reduced motion each step shows its finished frame for a
 * while (`STILL_HOLD_S`), then moves on. → and ← jump to the start of a step at any time and
 * play on from there; ← is not there on step 1. The last step stops on Get started, with
 * Play again above the controls, which starts again from step 1. Without `autoAdvance` (a
 * screen reader is on) each step stops at its end and waits for the arrows.
 *
 * The clock only runs on animation frames, so it waits while the page is hidden, and no
 * frame takes more than a tenth of a second.
 */

export const LAST_STEP = CHAPTERS.length - 1;

/** With reduced motion, how long each step's finished frame shows. */
export const STILL_HOLD_S = 3.5;

/** The longest frame the clock takes in one go, so a tab coming back does not skip a step. */
const MAX_FRAME_S = 0.1;

export interface PlayerState {
  /** The step on screen, 0-based. */
  step: number;
  /** The story's clock, in seconds. */
  t: number;
  playing: boolean;
  /** Seconds left before the next step starts by itself, or null when nothing will. */
  hold: number | null;
}

export interface PlayerMode {
  reduceMotion: boolean;
  /** Steps move on by themselves (not while a screen reader is on). */
  autoAdvance: boolean;
}

/** Where step `i` waits: just inside its own stretch, or the story's end for the last. */
export function stepEnd(i: number): number {
  return i === LAST_STEP ? WELCOME_END : CHAPTERS[i].t1 - 0.001;
}

/** Before step 1 has started (the intro is over it): on its first frame, nothing moving. */
export function initialPlayer(): PlayerState {
  return { step: 0, t: CHAPTERS[0].t0, playing: false, hold: null };
}

/** Step `i`'s finished frame, at rest: the parity gallery. */
export function shownAt(i: number): PlayerState {
  const step = clampStep(i);
  return { step, t: stepEnd(step), playing: false, hold: null };
}

/** With reduced motion, how long step `i`'s finished frame shows: none on the last step. */
function stillHold(i: number, mode: PlayerMode): number | null {
  if (i >= LAST_STEP || !mode.autoAdvance || !mode.reduceMotion) return null;
  return STILL_HOLD_S;
}

/**
 * Play step `i` from its start. With reduced motion, show its finished frame at once and
 * hold it.
 */
export function goTo(i: number, mode: PlayerMode): PlayerState {
  const step = clampStep(i);
  if (mode.reduceMotion) return { step, t: stepEnd(step), playing: false, hold: stillHold(step, mode) };
  return { step, t: CHAPTERS[step].t0, playing: true, hold: null };
}

/**
 * Run the clock `dt` seconds. With auto-advance the clock runs on through every step's end,
 * the step following it, and stops only at the story's end; the overshoot of the frame that
 * crosses a boundary is kept. Without it, a playing step stops at its own end. A hold (reduced
 * motion) that runs out starts the next step.
 */
export function tick(state: PlayerState, dt: number, mode: PlayerMode): PlayerState {
  const frame = Math.min(MAX_FRAME_S, Math.max(0, dt));
  if (state.playing) {
    const t = state.t + frame;
    if (mode.autoAdvance) {
      if (t >= WELCOME_END) return { ...state, step: LAST_STEP, t: WELCOME_END, playing: false, hold: null };
      let step = state.step;
      while (step < LAST_STEP && t >= CHAPTERS[step].t1) step += 1;
      return { ...state, step, t };
    }
    const end = stepEnd(state.step);
    if (t < end) return { ...state, t };
    return { ...state, t: end, playing: false, hold: null };
  }
  if (state.hold === null) return state;
  const hold = state.hold - frame;
  if (hold > 0) return { ...state, hold };
  return goTo(state.step + 1, mode);
}

/** Whether the clock has anything to do: the story playing, or a still frame's hold. */
export function running(state: PlayerState): boolean {
  return state.playing || state.hold !== null;
}

/** ← shows from step 2 on. */
export function backVisible(state: PlayerState): boolean {
  return state.step > 0;
}

/** On the last step → has grown into the Get started pill. */
export function showsGetStarted(state: PlayerState): boolean {
  return state.step === LAST_STEP;
}

/** The last step has played to the end: Play again shows above the controls. */
export function showsPlayAgain(state: PlayerState): boolean {
  return state.step === LAST_STEP && !state.playing && state.t >= WELCOME_END;
}

/** → was tapped: the next step from its start, or nothing on the last step (Get started). */
export function next(state: PlayerState, mode: PlayerMode): PlayerState {
  if (state.step >= LAST_STEP) return state;
  return goTo(state.step + 1, mode);
}

/** ← was tapped: the previous step, from its start. */
export function back(state: PlayerState, mode: PlayerMode): PlayerState {
  if (!backVisible(state)) return state;
  return goTo(state.step - 1, mode);
}

function clampStep(i: number): number {
  return Math.max(0, Math.min(LAST_STEP, i));
}
